import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '@stores/authStore';
import './Register.css';

const registerSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z
    .string()
    .min(12, 'Password must be at least 12 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
  confirmPassword: z.string(),
  first_name: z.string().min(2, 'First name must be at least 2 characters'),
  last_name: z.string().min(2, 'Last name must be at least 2 characters'),
  phone_number: z.string().optional(),
}).refine((data: { password: string; confirmPassword: string }) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type RegisterFormData = z.infer<typeof registerSchema>;

export const Register = () => {
  const { register: registerStaff, loading } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema as any) as any,
  });

  const password = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    setErrorMessage(null);
    
    console.log('[Register] Submitting registration for:', data.email);
    
    const result = await registerStaff({
      email: data.email,
      password: data.password,
      first_name: data.first_name,
      last_name: data.last_name,
      phone_number: data.phone_number,
    });

    console.log('[Register] Registration result:', result);

    if (result.success) {
      console.log('[Register] Registration successful, showing success screen');
      setRegistrationSuccess(true);
    } else {
      console.error('[Register] Registration failed:', result.message);
      setErrorMessage(result.message || 'Registration failed');
    }
  };

  if (registrationSuccess) {
    return (
      <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <img src="/src/assets/logo.gif" alt="Mal Bank" className="register-logo" />
          <h1 className="register-title">Registration Successful</h1>
        </div>

          <div className="alert alert-success">
            <strong>Thank you for registering!</strong>
            <p>Your account has been created and is pending administrator approval.</p>
            <p>You will be notified via email once your account is approved.</p>
          </div>

          <Link to="/login" className="btn btn-primary btn-large" style={{ width: '100%', textAlign: 'center' }}>
            Return to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <img src="/src/assets/logo.gif" alt="Mal Bank" className="register-logo" />
          <h1 className="register-title">Staff Registration</h1>
          <p className="register-subtitle">Register for Operations Dashboard Access</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="register-form">
          {errorMessage && (
            <div className="alert alert-error">
              {errorMessage}
            </div>
          )}

          <div className="form-row">
            <div className="input-wrapper">
              <label htmlFor="first_name" className="input-label">
                First Name
              </label>
              <input
                type="text"
                id="first_name"
                {...register('first_name')}
                className={`input ${errors.first_name ? 'input-error' : ''}`}
                placeholder="John"
                disabled={loading}
              />
              {errors.first_name && (
                <p className="input-error-message">{errors.first_name.message}</p>
              )}
            </div>

            <div className="input-wrapper">
              <label htmlFor="last_name" className="input-label">
                Last Name
              </label>
              <input
                type="text"
                id="last_name"
                {...register('last_name')}
                className={`input ${errors.last_name ? 'input-error' : ''}`}
                placeholder="Doe"
                disabled={loading}
              />
              {errors.last_name && (
                <p className="input-error-message">{errors.last_name.message}</p>
              )}
            </div>
          </div>

          <div className="input-wrapper">
            <label htmlFor="email" className="input-label">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              {...register('email')}
              className={`input ${errors.email ? 'input-error' : ''}`}
              placeholder="staff@malbank.com"
              autoComplete="email"
              disabled={loading}
            />
            {errors.email && (
              <p className="input-error-message">{errors.email.message}</p>
            )}
          </div>

          <div className="input-wrapper">
            <label htmlFor="phone_number" className="input-label">
              Phone Number (Optional)
            </label>
            <input
              type="tel"
              id="phone_number"
              {...register('phone_number')}
              className="input"
              placeholder="+971 50 123 4567"
              disabled={loading}
            />
          </div>

          <div className="input-wrapper">
            <label htmlFor="password" className="input-label">
              Password
            </label>
            <div className="input-with-icon">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                {...register('password')}
                className={`input ${errors.password ? 'input-error' : ''}`}
                placeholder="Create a strong password"
                autoComplete="new-password"
                disabled={loading}
              />
              <button
                type="button"
                className="input-icon-button"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {errors.password && (
              <p className="input-error-message">{errors.password.message}</p>
            )}
          </div>

          <div className="input-wrapper">
            <label htmlFor="confirmPassword" className="input-label">
              Confirm Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              {...register('confirmPassword')}
              className={`input ${errors.confirmPassword ? 'input-error' : ''}`}
              placeholder="Confirm your password"
              autoComplete="new-password"
              disabled={loading}
            />
            {errors.confirmPassword && (
              <p className="input-error-message">{errors.confirmPassword.message}</p>
            )}
          </div>

          <div className="password-requirements">
            <p className="text-small text-secondary">Password requirements:</p>
            <ul className="requirements-list text-small">
              <li className={password?.length >= 12 ? 'requirement-met' : ''}>
                At least 12 characters
              </li>
              <li className={/[A-Z]/.test(password || '') ? 'requirement-met' : ''}>
                One uppercase letter
              </li>
              <li className={/[a-z]/.test(password || '') ? 'requirement-met' : ''}>
                One lowercase letter
              </li>
              <li className={/[0-9]/.test(password || '') ? 'requirement-met' : ''}>
                One number
              </li>
              <li className={/[^A-Za-z0-9]/.test(password || '') ? 'requirement-met' : ''}>
                One special character
              </li>
            </ul>
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-large"
            disabled={loading}
            style={{ width: '100%' }}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <div className="register-footer">
            <p className="text-secondary text-small">
              Already have an account?{' '}
              <Link to="/login" className="link">
                Sign In
              </Link>
            </p>
          </div>
        </form>
      </div>

      <p className="register-disclaimer text-small text-secondary">
        By registering, you agree to follow Mal Bank staff policies and acknowledge that all actions are logged.
      </p>
    </div>
  );
};

